// フォーム関連のユーティリティ集。
// addAutoFunc（郵便番号→住所）/ numKeyFunc（数字キーボード）/ confirmForm（CF7 確認画面）。
//
// ============================================
// HTML 構造例:
// ============================================
// <form class="wpcf7 form">
//   <div data-inputs>...</div>
//   <div data-confirm hidden>...</div>
//   <button type="button" data-to-confirm>確認へ</button>
//   <div data-to-send hidden>
//     <button type="button" data-back>戻る</button>
//     <button type="button" data-send>送信</button>
//   </div>
// </form>
// ============================================
// CSS 注意:
// is-active / is-open 等の表示・トランジションは CSS 側で定義する。

import { bindOnce } from '../utils/bindOnce';
import { getScrollOffset, lenis } from './Scroll';

const CONFIG = {
  formSelector: '.wpcf7 form',              // CF7 フォーム
  zipFieldName: 'zip',                       // 郵便番号フィールド名
  addressFieldName: 'address',               // 住所フィールド名
  privacyFieldName: 'privacy',               // プライバシー同意チェックボックス名
  privacyCheckedLabel: '同意する',            // 確認画面の同意ラベル
  privacyUncheckedLabel: '未同意',            // 確認画面の未同意ラベル
  numInputSelector: '.js-num-input',         // テンキー表示対象の入力欄
  adrClass: 'h-adr',                         // yubinbango 用 Microformat クラス
  countryFieldSelector: '.p-country-name',   // 国情報 hidden フィールド
  countryFieldHtml: '<input type="hidden" class="p-country-name" value="Japan">', // 国情報 hidden の HTML
  postalCodeClass: 'p-postal-code',          // 郵便番号フィールドのクラス
  regionClass: 'p-region',                   // 都道府県フィールドのクラス
  localityClass: 'p-locality',               // 市区町村フィールドのクラス
  streetAddressClass: 'p-street-address',    // 番地フィールドのクラス
  extendedAddressClass: 'p-extended-address', // 建物名等フィールドのクラス
  inputsSelector: '[data-inputs]',           // 入力画面ラッパー
  confirmSelector: '[data-confirm]',         // 確認画面ラッパー
  toConfirmSelector: '[data-to-confirm]',    // 確認へボタン
  backSelector: '[data-back]',               // 戻るボタン
  sendSelector: '[data-send]',               // 送信ボタン
  sendWrapSelector: '[data-to-send]',         // 送信ボタンラッパー
  cf7SubmitSelector: '.wpcf7-submit',         // CF7 本来の送信ボタン
  cf7SpinnerClass: 'wpcf7-spinner',         // CF7 スピナーのクラス
  validationMessage: 'この項目は必須です',     // 必須エラーメッセージ
  thanksFallbackUrl: './thanks/',            // サンクスページのフォールバック URL
  errorSelector: '[data-error]',              // エラーメッセージ表示先
  cf7ResponseSelector: '.wpcf7-response-output', // CF7 レスポンス出力
  cf7NotValidSelector: '.wpcf7-not-valid',     // CF7 バリデーションエラー要素
  ariaInvalidSelector: '[aria-invalid="true"]', // aria-invalid 要素
  formTopSelector: '.c-form',                  // 確認画面切替時のスクロール先
  formScrollDuration: 1.2,                     // フォーム先頭へ戻る Lenis の duration
  formScrollLerp: 0.2,                         // フォーム先頭へ戻る Lenis の lerp
};

// yubinbango.js で郵便番号から住所を自動入力する。
// ライブラリは Microformat のクラス（h-adr / p-postal-code 等）を解釈して動作する。
function addAutoFunc() {
  const form     = document.querySelector(CONFIG.formSelector);
  const zipInput = document.querySelector(`[name="${CONFIG.zipFieldName}"]`);
  const addInput = document.querySelector(`[name="${CONFIG.addressFieldName}"]`);

  // 必要な要素と yubinbango.js の両方が揃っている場合のみ初期化する
  if (!form || !zipInput || !addInput || !window.YubinBango?.MicroformatDom) return;

  if (!bindOnce(form, 'yubinbango')) return;

  form.classList.add(CONFIG.adrClass);

  // 国情報の hidden フィールド（日本固定）
  if (!form.querySelector(CONFIG.countryFieldSelector)) {
    form.insertAdjacentHTML('afterbegin', CONFIG.countryFieldHtml);
  }

  zipInput.classList.add(CONFIG.postalCodeClass);

  // これらのクラスを持つフィールドに、都道府県・市区町村・番地が分割入力される
  addInput.classList.add(
    CONFIG.regionClass,          // 都道府県
    CONFIG.localityClass,        // 市区町村
    CONFIG.streetAddressClass,   // 番地
    CONFIG.extendedAddressClass  // 建物名等
  );

  new window.YubinBango.MicroformatDom();
}

// .js-num-input に inputmode="tel" を付与してモバイルでテンキーを表示する。
// numeric は一部端末で記号込みの IME になるため tel を使う。
function numKeyFunc() {
  const inputs = document.querySelectorAll(CONFIG.numInputSelector);
  if (inputs.length === 0) return;

  inputs.forEach((el) => {
    el.setAttribute('inputmode', 'tel');
  });
}

// data属性で指定した入力画面と確認画面を切り替える。
function confirmForm() {
  // CF7 のフィールド名に含まれる特殊文字を CSS セレクター用にエスケープする
  const escapeName = (name) => (window.CSS?.escape ? window.CSS.escape(name) : String(name).replace(/["\\]/g, '\\$&'));

  // "name[]" の形式の配列名から [] を取り除く（data-field との照合用）
  const normalizeName = (name) => String(name || '').replace(/\[\]$/, '');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // 確認／戻りのあとにフォーム先頭へ。固定ヘッダー分（PC/SP）を空けて着地する。
  // 入力⇔確認の切替で高さが変わるため、レイアウト確定後にスクロールする。
  const scrollToFormTop = (form) => {
    const target = form.closest(CONFIG.formTopSelector) || form;

    const run = () => {
      const top = target.getBoundingClientRect().top + window.scrollY;
      const destination = Math.max(0, top - getScrollOffset());

      if (prefersReducedMotion) {
        lenis.scrollTo(destination, { immediate: true });
        return;
      }

      lenis.scrollTo(destination, {
        lerp: CONFIG.formScrollLerp,
        duration: CONFIG.formScrollDuration,
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  };

  const ensureAccessibleNames = (form) => {
    form.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((field) => {
      if (field.labels?.length || field.hasAttribute('aria-label') || field.hasAttribute('aria-labelledby')) return;

      const visibleLabel = field.closest('.c-form__content')?.querySelector('.c-form__label');
      if (!visibleLabel) return;

      const labelCopy = visibleLabel.cloneNode(true);
      labelCopy.querySelector('.-required')?.remove();
      const labelText = labelCopy.textContent.trim();
      if (labelText) field.setAttribute('aria-label', labelText);
    });
  };

  document.querySelectorAll(CONFIG.formSelector).forEach((form) => {
    ensureAccessibleNames(form);

    const inputsWrap   = form.querySelector(CONFIG.inputsSelector);
    const confirmWrap  = form.querySelector(CONFIG.confirmSelector);
    const toConfirmBtn = form.querySelector(CONFIG.toConfirmSelector);
    const backBtn      = form.querySelector(CONFIG.backSelector);
    const sendBtn      = form.querySelector(CONFIG.sendSelector);
    const sendWrap     = form.querySelector(CONFIG.sendWrapSelector);
    const cf7Submits   = form.querySelectorAll(CONFIG.cf7SubmitSelector);

    // 必要な要素が1つでも欠けていれば初期化しない
    if (!inputsWrap || !confirmWrap || !toConfirmBtn || !backBtn || !sendBtn || cf7Submits.length === 0) return;

    // 要素が揃った時点でフラグを立てたいので、要素チェックの後に置く
    if (!bindOnce(form, 'cf7Confirm')) return;

    const hide = (el) => {
      if (!el) return;
      el.setAttribute('hidden', '');
      el.style.display = 'none';
    };

    const show = (el, display = '') => {
      if (!el) return;
      el.removeAttribute('hidden');
      el.style.display = display;
    };

    const disableSending = () => {
      sendBtn.disabled = true;
      sendBtn.setAttribute('aria-busy', 'true');
      cf7Submits.forEach((btn) => {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      });
    };

    const enableSending = () => {
      sendBtn.disabled = false;
      sendBtn.removeAttribute('aria-busy');
      cf7Submits.forEach((btn) => {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      });
    };

    // CF7 本来の送信ボタンはカスタム UI で代替するため常に隠す
    const hideCf7Submits = () => {
      cf7Submits.forEach((btn) => {
        hide(btn);
        const spinner = btn.nextElementSibling;
        if (spinner?.classList.contains(CONFIG.cf7SpinnerClass)) spinner.style.display = 'none';
      });
    };

    // 初期状態
    show(inputsWrap);
    show(toConfirmBtn);
    hide(confirmWrap);
    hide(sendWrap);
    hide(backBtn);
    hide(sendBtn);
    hideCf7Submits();

    // CF7 の必須項目を検査し、未入力があれば reportValidity() で標準のエラー表示をする
    const validateBeforeConfirm = () => {
      form.querySelectorAll('[name]').forEach((el) => el.setCustomValidity?.(''));

      const required = form.querySelectorAll('[name][required], [name][aria-required="true"], .wpcf7-validates-as-required [name]');

      const checkedGroups = new Set();
      let firstInvalid    = null;

      required.forEach((el) => {
        const name = el.name;
        if (!name) return;

        if (el.type === 'checkbox' || el.type === 'radio') {
          // 同名グループで1つでも選択されていれば OK
          if (checkedGroups.has(name)) return;
          checkedGroups.add(name);

          const group = form.querySelectorAll(`[name="${escapeName(name)}"]`);
          if (!Array.from(group).some((i) => i.checked)) {
            const head = group[0];
            head?.setCustomValidity?.(CONFIG.validationMessage);
            if (!firstInvalid) firstInvalid = head;
          }
        } else if (el.tagName === 'SELECT') {
          if (!el.value) {
            el.setCustomValidity(CONFIG.validationMessage);
            if (!firstInvalid) firstInvalid = el;
          }
        } else {
          if (!(el.value || '').trim()) {
            el.setCustomValidity(CONFIG.validationMessage);
            if (!firstInvalid) firstInvalid = el;
          }
        }
      });

      const isValid = form.reportValidity();
      if (!isValid && firstInvalid) firstInvalid.focus();
      return isValid;
    };

    // 各 [name] 要素の値を読み取り、[data-field] のスロットに textContent で書き出す
    const populateConfirm = () => {
      // 同名グループ（checkbox / radio）を要素数ぶん重複処理しないための記録
      const processedNames = new Set();

      form.querySelectorAll('[name]').forEach((el) => {
        const name = el.name;
        if (!name) return;

        // 値はグループ全体から取るので、同じ name は最初の1回だけ処理する
        if (processedNames.has(name)) return;
        processedNames.add(name);

        const fieldName = normalizeName(name);
        let value       = '';

        if (el.type === 'checkbox') {
          const checked = form.querySelectorAll(`input[name="${escapeName(name)}"]:checked`);
          // privacy は「同意する / 未同意」のラベルに変換する
          if (fieldName === CONFIG.privacyFieldName) {
            value = checked.length ? CONFIG.privacyCheckedLabel : CONFIG.privacyUncheckedLabel;
          } else {
            value = Array.from(checked)
              .map((i) => i.value)
              .join(', ');
          }
        } else if (el.type === 'radio') {
          const checked = form.querySelector(`input[name="${escapeName(name)}"]:checked`);
          value = checked ? checked.value : '';
        } else if (el.tagName === 'SELECT') {
          value = el.value || '';
        } else {
          value = el.value ?? '';
        }

        const slot = form.querySelector(`[data-field="${escapeName(name)}"], [data-field="${escapeName(fieldName)}"]`);
        if (slot) slot.textContent = value;
      });
    };

    // 「確認へ」: バリデーション → 確認画面
    toConfirmBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (!validateBeforeConfirm()) return;

      populateConfirm();
      hide(inputsWrap);
      show(confirmWrap);
      hide(toConfirmBtn);
      hideCf7Submits();
      show(sendWrap);
      show(backBtn);
      show(sendBtn);
      if (!confirmWrap.hasAttribute('tabindex')) confirmWrap.setAttribute('tabindex', '-1');
      confirmWrap.focus({ preventScroll: true });
      scrollToFormTop(form);
    });

    // 「送信する」: 隠してある CF7 本来の送信ボタンをクリックして Ajax 送信する
    sendBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (sendBtn.disabled) return;
      cf7Submits[0]?.click();
    });

    // 「戻る」: 入力画面へ（CF7 本来の送信ボタンは出さない）
    backBtn.addEventListener('click', (e) => {
      e.preventDefault();
      show(inputsWrap);
      hide(confirmWrap);
      show(toConfirmBtn);
      hideCf7Submits();
      hide(sendWrap);
      hide(backBtn);
      hide(sendBtn);

      // 最初の入力要素にフォーカスを戻す
      const firstInput = inputsWrap.querySelector('input, textarea, select');
      firstInput?.focus({ preventScroll: true });
      scrollToFormTop(form);
    });

    // 送信開始時にボタンを無効化してダブル送信を防ぐ
    form.addEventListener('wpcf7beforesubmit', () => {
      disableSending();
    });

    // エラー時は入力画面に戻してエラーを表示する
    const handleError = (apiResponse) => {
      if (!confirmWrap.hasAttribute('hidden')) backBtn.click();

      // CF7 が描画したエラーメッセージを [data-error] スロットにも転記する
      const msg = form.querySelector(CONFIG.cf7ResponseSelector)?.textContent?.trim();
      if (msg) {
        const errorBox = form.querySelector(CONFIG.errorSelector);
        if (errorBox) {
          errorBox.textContent = msg;
          errorBox.setAttribute('role', 'alert');
          errorBox.removeAttribute('hidden');
        }
      }

      // nonce 期限切れ等ならフォームを再初期化する
      const code = apiResponse?.code || apiResponse?.status;
      if (code === 'rest_invalid_nonce' || code === 'aborted') {
        try {
          window.wpcf7?.init?.(form);
        } catch {
          // 再初期化の失敗は無視する
        }
      }

      enableSending();

      // エラー箇所までスクロールしてフォーカスを移す
      const errorTarget = form.querySelector(CONFIG.cf7NotValidSelector) || form.querySelector(CONFIG.ariaInvalidSelector) || form.querySelector(CONFIG.cf7ResponseSelector);
      if (errorTarget) {
        if (!errorTarget.matches('input, select, textarea, button, a[href], [tabindex]')) {
          errorTarget.setAttribute('tabindex', '-1');
        }
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        errorTarget.scrollIntoView({ behavior, block: 'center' });
        errorTarget.focus();
      }
    };

    form.addEventListener('wpcf7invalid', (e) => handleError(e.detail?.apiResponse));
    form.addEventListener('wpcf7spam', (e) => handleError(e.detail?.apiResponse));
    form.addEventListener('wpcf7mailfailed', (e) => handleError(e.detail?.apiResponse));

    // 送信結果の統合ハンドラ
    form.addEventListener('wpcf7submit', (e) => {
      const status = e.detail?.apiResponse?.status;

      if (status === 'mail_sent') {
        window.location.assign(window.mythemeConfig?.thanksUrl || CONFIG.thanksFallbackUrl);
      } else {
        handleError(e.detail?.apiResponse);
      }

      enableSending();
    });

    // CF7 のリセット時は入力画面に戻す
    form.addEventListener('reset', () => {
      if (!confirmWrap.hasAttribute('hidden')) backBtn.click();
      enableSending();
    });
  });
}

export { addAutoFunc, numKeyFunc, confirmForm };
